import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { PageHeader } from '@/lib/page-header'
import { z } from 'zod'
import { useState } from 'react'
import { Check } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/lib/auth'
import { getTheme, setTheme, type Theme } from '@/lib/theme'

const searchSchema = z.object({
  tab: z.enum(['profile', 'appearance', 'settings']).optional().default('profile'),
})

export const Route = createFileRoute('/_shell/account')({
  validateSearch: searchSchema,
  component: AccountPage,
})

// ─── Profile tab ──────────────────────────────────────────────────────────────

function ProfileTab() {
  const { currentEmail } = useAuth()
  const [saved, setSaved] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-base font-semibold text-foreground">Manage Profile</h2>
        <p className="text-sm text-muted-foreground">
          Change your profile picture, email, full name, and username.
        </p>
      </div>
      <div className="border-t border-border" />

      <form onSubmit={handleSubmit}>
        <Row label="Profile Picture">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-medium">
            {currentEmail?.[0]?.toUpperCase() ?? '?'}
          </div>
        </Row>
        <div className="border-t border-border" />

        <Row label="Email">
          <div className="relative w-72">
            <Input defaultValue={currentEmail ?? ''} className="pr-9" />
            <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
          </div>
        </Row>
        <div className="border-t border-border" />

        <Row label="First Name">
          <Input defaultValue="" placeholder="First name" className="w-72" />
        </Row>
        <div className="border-t border-border" />

        <Row label="Last Name">
          <Input defaultValue="" placeholder="Last name" className="w-72" />
        </Row>
        <div className="border-t border-border" />

        <div className="py-5">
          <Button type="submit" size="sm" className="gap-1.5">
            <Check className="h-3.5 w-3.5" />
            {saved ? 'Saved' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}

// ─── Appearance tab ───────────────────────────────────────────────────────────

function AppearanceTab() {
  const [theme, setThemeState] = useState<Theme>(getTheme)

  function handleThemeChange(value: Theme) {
    setThemeState(value)
    setTheme(value)
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-base font-semibold text-foreground">Appearance</h2>
        <p className="text-sm text-muted-foreground">
          Customize the appearance of your dashboard.
        </p>
      </div>
      <div className="border-t border-border" />

      <Row label="Theme">
        <Select value={theme} onValueChange={handleThemeChange}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="light">Light Mode</SelectItem>
            <SelectItem value="dark">Dark Mode</SelectItem>
            <SelectItem value="system">System Mode</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <div className="border-t border-border" />
    </div>
  )
}

// ─── Settings tab ─────────────────────────────────────────────────────────────

function SettingsTab() {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-base font-semibold text-foreground">Settings</h2>
        <p className="text-sm text-muted-foreground">Manage your account settings.</p>
      </div>
      <div className="border-t border-border" />
      <div className="py-10 text-sm text-muted-foreground">No settings configured yet.</div>
    </div>
  )
}

// ─── Shared row helper ────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-4">
      <Label className="text-sm text-foreground">{label}</Label>
      {children}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function AccountPage() {
  const { tab } = useSearch({ from: '/_shell/account' })
  const navigate = useNavigate()

  return (
    <div className="p-8 max-w-2xl">
      <PageHeader title="Account" subtitle="View and manage your account." />
      <Tabs
        value={tab}
        onValueChange={(v) =>
          navigate({ to: '/account', search: { tab: v as 'profile' | 'appearance' | 'settings' } })
        }
      >
        <TabsList variant="line" className="w-full justify-start border-b border-border rounded-none h-auto pb-0 mb-6">
          {(['profile', 'appearance', 'settings'] as const).map((t) => (
            <TabsTrigger key={t} value={t} className="capitalize">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile"><ProfileTab /></TabsContent>
        <TabsContent value="appearance"><AppearanceTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  )
}
